import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";

const REFERRAL_REASONS = [
  { id: "disability", label: "Disability determination / documentation" },
  { id: "workplace", label: "Workplace accommodation or return-to-work" },
  { id: "adhd", label: "ADHD concerns" },
  { id: "ptsd", label: "Trauma / PTSD" },
  { id: "college", label: "College accommodation" },
  { id: "diagnostic", label: "Diagnostic clarification" },
  { id: "other", label: "Other" },
];
const REQUESTED_BY = ["Self", "Physician", "Therapist", "Attorney", "Employer", "School / college", "Government / benefits agency", "Other"];
const DOC_TYPES = ["Diagnostic information", "Functional limitations", "Occupational functioning", "Academic functioning", "Accommodation recommendations", "Treatment recommendations"];
const RECORDS = ["Prior psychological evaluations", "Psychiatric records", "Therapy records", "Medical records", "School records", "Employment records", "Disability paperwork", "None available"];

const STEPS = ["Referral", "About you", "Clinical history", "Review"];

export default function IntakeWizard() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    referral_question: "", requested_by: "Self", documentation_requested: [], specific_question: "",
    dob: "", phone: "", occupation: "", employer_or_school: "",
    diagnoses: "", current_medications: "", prior_treatment: "", hospitalizations: "", prior_evaluations: "",
    records_available: [], trauma_flag: false, attention_flag: false, substance_flag: false,
  });

  useEffect(() => {
    api.get(`/cases/${caseId}`).then((r) => {
      if (r.data.intake) setForm((f) => ({ ...f, ...r.data.intake }));
      if (!["awaiting_intake", "questionnaires_pending"].includes(r.data.status)) navigate("/app");
    }).catch((e) => toast.error(errMsg(e)));
  }, [caseId, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleList = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  const progress = useMemo(() => ((step + 1) / STEPS.length) * 100, [step]);

  const canNext = step === 0 ? !!form.referral_question : step === 1 ? !!form.dob : true;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/cases/${caseId}/intake`, form);
      toast.success(`Intake complete — ${data.measures_assigned.length} measures assigned.`);
      navigate(`/app/case/${caseId}/questionnaires`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell title="Confidential intake" subtitle="Your answers shape which validated measures are assigned to your evaluation.">
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-3">
          <p className="label-caps text-[#7E9B82]" data-testid="intake-step-label">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
        </div>
        <Progress value={progress} className="h-2 mb-10 [&>div]:bg-[#1E3F20] transition-all" data-testid="intake-progress" />

        <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {step === 0 && (
            <div className="space-y-8">
              <div>
                <p className="font-heading text-2xl font-semibold mb-4">What do you need this evaluation for?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {REFERRAL_REASONS.map((r) => (
                    <button key={r.id} type="button" data-testid={`referral-${r.id}`}
                      onClick={() => set("referral_question", r.label)}
                      className={`text-left p-4 rounded-xl border text-sm transition-colors ${form.referral_question === r.label ? "border-[#1E3F20] bg-[#1E3F20]/5 font-semibold" : "border-[#E5E5DF] bg-white hover:border-[#7E9B82]"}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-heading text-xl font-semibold mb-4">Who requested the evaluation?</p>
                <div className="flex flex-wrap gap-2">
                  {REQUESTED_BY.map((r) => (
                    <button key={r} type="button" data-testid={`requested-by-${r.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      onClick={() => set("requested_by", r)}
                      className={`px-4 py-2 rounded-full border text-sm transition-colors ${form.requested_by === r ? "border-[#1E3F20] bg-[#1E3F20] text-white" : "border-[#E5E5DF] bg-white hover:border-[#7E9B82]"}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-heading text-xl font-semibold mb-4">What documentation is requested?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {DOC_TYPES.map((d) => (
                    <label key={d} className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E5DF] bg-white text-sm cursor-pointer hover:border-[#7E9B82] transition-colors">
                      <Checkbox data-testid={`doc-type-${d.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        checked={form.documentation_requested.includes(d)} onCheckedChange={() => toggleList("documentation_requested", d)} />
                      {d}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>What specific question should this evaluation answer?</Label>
                <Textarea data-testid="intake-specific-question" rows={3} value={form.specific_question}
                  onChange={(e) => set("specific_question", e.target.value)} className="input-warm border-[#E5E5DF]"
                  placeholder="e.g., Does my anxiety condition limit my ability to maintain consistent attendance at work?" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2"><Label>Date of birth</Label>
                <Input type="date" data-testid="intake-dob" value={form.dob} onChange={(e) => set("dob", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
              <div className="space-y-2"><Label>Phone</Label>
                <Input data-testid="intake-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
              <div className="space-y-2"><Label>Occupation</Label>
                <Input data-testid="intake-occupation" value={form.occupation} onChange={(e) => set("occupation", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
              <div className="space-y-2"><Label>Employer or school</Label>
                <Input data-testid="intake-employer" value={form.employer_or_school} onChange={(e) => set("employer_or_school", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="space-y-2"><Label>Current or previous mental-health diagnoses</Label>
                <Textarea data-testid="intake-diagnoses" rows={2} value={form.diagnoses} onChange={(e) => set("diagnoses", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
              <div className="space-y-2"><Label>Current medications</Label>
                <Textarea data-testid="intake-medications" rows={2} value={form.current_medications} onChange={(e) => set("current_medications", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
              <div className="space-y-2"><Label>Prior therapy or psychiatric treatment</Label>
                <Textarea data-testid="intake-prior-treatment" rows={2} value={form.prior_treatment} onChange={(e) => set("prior_treatment", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2"><Label>Hospitalizations</Label>
                  <Textarea data-testid="intake-hospitalizations" rows={2} value={form.hospitalizations} onChange={(e) => set("hospitalizations", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
                <div className="space-y-2"><Label>Prior evaluations / testing</Label>
                  <Textarea data-testid="intake-prior-evaluations" rows={2} value={form.prior_evaluations} onChange={(e) => set("prior_evaluations", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
              </div>
              <div>
                <p className="font-heading text-xl font-semibold mb-3">Records available</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {RECORDS.map((r) => (
                    <label key={r} className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E5DF] bg-white text-sm cursor-pointer hover:border-[#7E9B82] transition-colors">
                      <Checkbox data-testid={`records-${r.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        checked={form.records_available.includes(r)} onCheckedChange={() => toggleList("records_available", r)} />
                      {r}
                    </label>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-[#E3DAC9] bg-[#E3DAC9]/30 p-5 space-y-3">
                <p className="font-semibold text-sm">A few more questions help us assign the right measures:</p>
                {[
                  ["trauma_flag", "I have experienced a traumatic event that still affects me", "intake-flag-trauma"],
                  ["attention_flag", "I have significant concerns about attention, focus, or organization", "intake-flag-attention"],
                  ["substance_flag", "I would like my alcohol or substance use considered in this evaluation", "intake-flag-substance"],
                ].map(([k, label, tid]) => (
                  <label key={k} className="flex items-start gap-3 text-sm cursor-pointer">
                    <Checkbox data-testid={tid} checked={form[k]} onCheckedChange={(v) => set(k, !!v)} className="mt-0.5" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="card-warm p-8 space-y-4" data-testid="intake-review">
              <p className="font-heading text-2xl font-semibold">Review & submit</p>
              <div className="text-sm space-y-2 text-[#5C5C57]">
                <p><span className="font-semibold text-[#1C1C19]">Reason:</span> {form.referral_question}</p>
                <p><span className="font-semibold text-[#1C1C19]">Requested by:</span> {form.requested_by}</p>
                <p><span className="font-semibold text-[#1C1C19]">Documentation:</span> {form.documentation_requested.join(", ") || "—"}</p>
                <p><span className="font-semibold text-[#1C1C19]">Records:</span> {form.records_available.join(", ") || "—"}</p>
              </div>
              <p className="text-xs text-[#5C5C57]">After submitting, your validated questionnaires will be assigned. Your clinician reviews everything before your interview.</p>
            </div>
          )}
        </motion.div>

        <div className="flex justify-between mt-10">
          <Button variant="ghost" data-testid="intake-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)}
            className="rounded-full text-[#5C5C57]"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
          {step < 3 ? (
            <Button data-testid="intake-next-btn" disabled={!canNext} onClick={() => setStep(step + 1)}
              className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
              Continue <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button data-testid="intake-submit-button" disabled={saving} onClick={submit}
              className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
              {saving ? "Submitting…" : "Submit intake"}
            </Button>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
